import { Assert } from '@cotera/utilities';
import React from 'react';
import { CollapsibleError, Icon, Tooltip } from '../../app/components';
import { FilterBuilder, useOptions } from '../../app/components/app';
import { Text } from '../../app/components/ui';
import { TemplatedExpressionEditor } from '@cotera/client/app/components/eraql-editor';
import {
  ModelScope,
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '../actions/framework';
import { TabIndicator } from '../components/topbar/tab';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ButtonGroup } from '../components/ui/button-group';
import { GraphDataGrid } from '../data-grid/managed-data-grid';
import { GraphModel } from '../graph/graph.model';
import { displayShortcut } from '../lib/utils';
import { useTabParams } from '../tab-manager/tab-manager';
import { FormatAction } from './actions/format.action';
import { NewColumnAction } from './actions/new-column.action';
import { RunDatasetChangesAction } from './actions/run-changes.action';
import { RunOnSourceAction } from './actions/run-on-source.action';
import { SaveDatasetAction } from './actions/save-dataset.action';
import { ToggleEditorAction } from './actions/toggle-editor.action';
import { DatasetModel } from './model/dataset.model';
import { Button } from '../components/ui/button';
import { CreateViewAction } from './actions/create-view.action';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../components/ui/resizable';
import { useContainerSize } from '../components/data-grid/use-container-size';
import { DuplicateAction } from '../graph/duplicate.action';
import { usePreviewCellRenderer } from '../columns/llm/components/preview-cell-renderer';
import {
  getEditorStateFromGraph,
  getLlmConfigFromGraph,
} from '../columns/llm/utils/get-editor-state-from-graph';
import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { DataGridModel } from '../data-grid/model';

const useDataset = () => {
  const { id } = useTabParams<{ id: string }>();
  const model = useInScopeModel(DatasetModel, id) as DatasetModel;

  Assert.assert(model !== null, 'Dataset not found');
  return model;
};

export const Dataset: React.FC<{
  isNew: boolean;
  startingEraQl?: string;
  id: string;
  name?: string;
}> = ({ isNew, startingEraQl, id, name }) => {
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const model = useMemo(
    () =>
      new DatasetModel(
        {
          name: name ? name : isNew ? 'New Dataset' : id,
          graph: graphModel.graph,
          compiler: graphModel.compiler,
        },
        {
          id,
          isNew,
          startingEraQl,
        }
      ),
    [id, isNew, startingEraQl, name, graphModel]
  );

  return (
    <>
      {/*
        This is the tab that is used to display the dataset name in the tab bar.
        We hoist is above the model scope to ensure that the tab is rendered before the model is scoped.
      */}
      <TabIndicator model={model} />
      <ModelScope models={[model]}>
        <NewDatasetInner />
      </ModelScope>
    </>
  );
};

const Header: React.FC = () => {
  const runAction = useAction(RunDatasetChangesAction);
  const model = useDataset();
  const status = useWatchableValue(model.status);
  const toggleEditor = useAction(ToggleEditorAction);
  const formatEraql = useAction(FormatAction);
  const saveChanges = useAction(SaveDatasetAction);
  const runOnSource = useAction(RunOnSourceAction);
  const newColumn = useAction(NewColumnAction);
  const createChildDataset = useAction(CreateViewAction);
  const duplicateDataset = useAction(DuplicateAction);

  return (
    <div className="flex items-center justify-between pb-4  border-b border-divider">
      <HeaderStatusSwitcher status={status} />
      <div className="flex items-center">
        <Tooltip
          side="left"
          tooltipContent={`Apply - ${displayShortcut(
            runAction.action?.shortcut ?? []
          )}`}
        >
          <Button
            variant={'primary'}
            size={'sm'}
            onClick={() => runAction.execute()}
            className="ml-2"
          >
            <Icon icon="filter" />
            Apply
          </Button>
        </Tooltip>
        <Tooltip
          side="bottom"
          tooltipContent={displayShortcut(saveChanges.action?.shortcut ?? [])}
        >
          <Button
            variant={'default'}
            size={'sm'}
            className="ml-2"
            onClick={async () => await saveChanges.execute()}
          >
            <Icon icon="save" /> Save
          </Button>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="!outline-hidden -mr-1! p-0!"
              variant={'ghost'}
              size={'icon-sm'}
            >
              <Icon icon="ellipsis-vertical" size="large" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56">
            <DropdownMenuItem onSelect={() => newColumn.execute()}>
              New Column
              <DropdownMenuShortcut shortcut={newColumn.action?.shortcut} />
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={async () => await createChildDataset.execute()}
            >
              Create a child dataset
              <DropdownMenuShortcut
                shortcut={createChildDataset.action?.shortcut}
              />
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleEditor.execute()}>
              Toggle Editor
              <DropdownMenuShortcut shortcut={toggleEditor.action?.shortcut} />
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => runOnSource.execute()}>
              Refresh Data
              <DropdownMenuShortcut shortcut={runOnSource.action?.shortcut} />
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => formatEraql.execute()}>
              Format EraQL
              <DropdownMenuShortcut shortcut={formatEraql.action?.shortcut} />
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => duplicateDataset.execute()}>
              Duplicate Dataset
              <DropdownMenuShortcut
                shortcut={duplicateDataset.action?.shortcut}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const HeaderStatusSwitcher: React.FC<{
  status: 'ok' | 'error' | 'empty';
}> = ({ status }) => {
  const model = useDataset();
  const item = useWatchableValue(model.item);
  const errors = useWatchableValue(model.errors);

  switch (status) {
    case 'ok':
      return item !== null ? <Filter /> : <div />;
    case 'error':
      return (
        <div className="flex items-center mb-2">
          <Icon icon="exclamation-triangle" className="mr-2 text-destructive" />
          <Text.Caption className="text-destructive">
            Dataset is in a broken state.
          </Text.Caption>
          {errors.map((error) => (
            <CollapsibleError
              key={error.toString()}
              error={error}
              summary="Dataset is in a broken state."
            />
          ))}
        </div>
      );
    case 'empty':
      return (
        <div className="flex items-center mb-2">
          <Icon icon="information-circle" className="mr-2" />
          <Text.Caption>
            Dataset is empty - write a query to get started.
          </Text.Caption>
        </div>
      );
    default:
      return <div />;
  }
};

const Filter: React.FC<{}> = () => {
  const model = useDataset();
  const item = useWatchableValue(model.item);

  Assert.assert(item !== null, 'Rel not found');
  const rel = item.rel;
  const options = useOptions(rel.attributes);
  const filters = useWatchableValue(model.filterGroup);

  return (
    <FilterBuilder
      //hack to force a re-render when the filters change
      // key={JSON.stringify(filters)}
      dangerouslyOptInToDynamicOptions
      filterGroup={filters}
      options={options}
      autoRun={false}
      showRun={false}
      rel={rel}
      runEnabled
      runVariants={[
        {
          label: 'Refresh Data',
          id: 'source',
          icon: 'filter',
        },
      ]}
      onChange={({ filterGroup }) => {
        model.filterGroup.set(filterGroup);
      }}
      onRun={() => {
        //run is handled by the dataset model
      }}
    />
  );
};

const NewDatasetInner = () => {
  const model = useDataset();
  Assert.assert(model !== null, 'Model not found');
  const rel = useWatchableValue(model.preview);
  const hasBeenTargeted = useWatchableValue(model.hasBeenTargeted);
  const newColumn = useAction(NewColumnAction);
  const isCollapsed = useWatchableValue(model.isEditorCollapsed);

  // Create getEditorState function that works per column
  const getEditorState = useMemo(() => {
    return (columnName: string) => {
      const editorState = getEditorStateFromGraph(
        model.graph.snapshot(),
        model.id,
        columnName
      );
      if (!editorState) {
        // Return empty document state if column not found
        return { type: 'doc' as const, content: [] };
      }
      return editorState;
    };
  }, [model]);

  // Create getLlmConfig function that works per column
  const getLlmConfig = useMemo(() => {
    return (columnName: string) => {
      return getLlmConfigFromGraph(
        model.graph.snapshot(),
        model.id,
        columnName
      );
    };
  }, [model]);

  // Create preview cell renderer
  const previewCellRenderer = usePreviewCellRenderer({
    getEditorState,
    getLlmConfig,
    relName: model.id,
  });

  return (
    <div className="flex gap-4 h-full w-full py-4">
      <div className="flex flex-col gap-4 w-full h-full">
        <Header />

        {!isCollapsed ? (
          <ResizablePanelGroup direction="vertical" className="h-full">
            <ResizablePanel defaultSize={rel !== null ? 40 : 100} minSize={20}>
              <Editor />
            </ResizablePanel>

            {rel !== null && (
              <>
                <ResizableHandle className="my-2" />

                <ResizablePanel defaultSize={60} minSize={30}>
                  <div className="pt-1 h-full">
                    <GraphDataGrid
                      rel={rel}
                      managedProps={model}
                      relName={model.id}
                      buildExplorer={() => model.graph.snapshot()}
                      shouldShow={hasBeenTargeted}
                      AtcCellComponent={previewCellRenderer}
                      actions={
                        <ButtonGroup>
                          <Button
                            size="default"
                            variant="outline"
                            className="cursor-pointer"
                            onClick={() => newColumn.execute({ type: 'llm' })}
                          >
                            <Icon icon="add" />
                            Add Agent
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="icon"
                                variant="outline"
                                className="cursor-pointer"
                              >
                                <Icon icon="chevron-down" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onSelect={() =>
                                  newColumn.execute({ type: 'expression' })
                                }
                              >
                                <Icon icon="code-bracket" />
                                Formula
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onSelect={() =>
                                  newColumn.execute({ type: 'taxonomy' })
                                }
                              >
                                <Icon icon="list-tree" />
                                Taxonomy
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer"
                                onSelect={() =>
                                  newColumn.execute({ type: 'tool-call' })
                                }
                              >
                                <Icon icon="workflow" />
                                Tool Call
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </ButtonGroup>
                      }
                    />
                  </div>
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
        ) : rel !== null ? (
          <GraphDataGrid
            rel={rel}
            managedProps={model}
            relName={model.id}
            buildExplorer={() => model.graph.snapshot()}
            shouldShow={hasBeenTargeted}
            AtcCellComponent={previewCellRenderer}
            actions={
              <ButtonGroup>
                <Button
                  size="default"
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => newColumn.execute({ type: 'llm' })}
                >
                  <Icon icon="add" />
                  Add Agent
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      className="cursor-pointer"
                    >
                      <Icon icon="chevron-down" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={() => newColumn.execute({ type: 'expression' })}
                    >
                      <Icon icon="code-bracket" />
                      Formula
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={() => newColumn.execute({ type: 'taxonomy' })}
                    >
                      <Icon icon="list-tree" />
                      Taxonomy
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onSelect={() => newColumn.execute({ type: 'tool-call' })}
                    >
                      <Icon icon="workflow" />
                      Tool Call
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </ButtonGroup>
            }
          />
        ) : null}
      </div>
    </div>
  );
};

const Editor: React.FC = () => {
  const model = useDataset();
  const { eraql } = useWatchableValue(model.formState);
  const graph = useWatchableValue(model.graph);

  const { containerRef, containerSize } = useContainerSize();

  // Calculate editor height based on container size
  // Reserve space for gap-4 (16px) and any internal padding
  const editorHeight = containerSize.height
    ? Math.max(containerSize.height - 32, 100)
    : 200;

  return (
    <div ref={containerRef} className="flex flex-col gap-4 h-full">
      <TemplatedExpressionEditor
        templated={false}
        type="rel"
        eraql={eraql}
        height={editorHeight}
        parser={graph.parser}
        graph={graph}
        onChange={async (value) => {
          model.formState.set({
            ...model.formState.snapshot(),
            eraql: value,
          });
        }}
      />
    </div>
  );
};

const ArtifactTimestampWithModel: React.FC<{
  model: DataGridModel;
  className?: string;
}> = ({ model, className }) => {
  const artifactMeta = model.artifactMeta;

  if (artifactMeta === null || artifactMeta.t !== 'file') {
    return null;
  }

  const date =
    artifactMeta.createdAt instanceof Date
      ? artifactMeta.createdAt
      : new Date(artifactMeta.createdAt);

  const formattedDate = DateTime.fromJSDate(date)
    .toUTC()
    .toLocaleString(DateTime.DATETIME_MED);

  return (
    <div className={className ?? 'flex justify-center py-2'}>
      <Text.Caption>Data last updated on {formattedDate} UTC</Text.Caption>
    </div>
  );
};

export const ArtifactTimestamp: React.FC<{
  model?: DataGridModel;
  className?: string;
}> = ({ model, className }) => {
  if (model !== undefined) {
    return <ArtifactTimestampWithModel model={model} className={className} />;
  }

  return <ArtifactTimestampFromContext className={className} />;
};

const ArtifactTimestampFromContext: React.FC<{
  className?: string;
}> = ({ className }) => {
  const datasetModel = useDataset();
  const dataGridModel = useInScopeModel(DataGridModel, datasetModel.id);

  if (dataGridModel === null) {
    return null;
  }

  return (
    <ArtifactTimestampWithModel model={dataGridModel} className={className} />
  );
};
